import type { QueryClient } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'
import { Storage, Zone } from 'viem/tempo'

/**
 * The zone chain wallet-next currently supports.
 *
 * Zone A on Tempo testnet.
 */
export const chain = Zone.a

/** Numeric Zone A id. */
export const zoneId = 6 as const

/**
 * Zone A RPC URL.
 */
export const rpcUrl = chain.rpcUrls.default.http[0]

/**
 * Storage key the `viem/tempo` `http()` transport reads to stamp
 * `X-Authorization-Token` on every JSON-RPC request to the zone.
 */
export const authTokenKey = `auth:token:${chain.id}` as const

/** React Query key for the persisted zone authorization token. */
export const queryKey = ['zone', 'auth-token', chain.id] as const

/**
 * Clears the persisted zone auth token and the cached `useAuthToken`
 * query for the current zone chain. Subsequent RPC calls (and any
 * worker fetch that re-stamps the bearer token) will be unauthenticated
 * until the user re-runs `useSignAuthorizationToken`.
 */
export async function clear(queryClient: QueryClient) {
  await Storage.defaultStorage().removeItem(authTokenKey)
  queryClient.setQueryData(queryKey, null)
}

/**
 * Returns the persisted zone auth token, or `null` if none is stored.
 *
 * We can't validate the token without calling
 * `zone_getAuthorizationTokenInfo`, which itself requires the token —
 * so the wallet treats "token present in storage" as "zone unlocked".
 * If the token has expired, the next RPC will 401 and the user can
 * re-unlock.
 */
export async function getAuthToken() {
  return Storage.defaultStorage().getItem(authTokenKey)
}

/**
 * Reactively reads the persisted zone auth token. Used to gate features
 * (private balances, in-zone send) on whether the user has unlocked
 * the zone on this device.
 *
 * The query key is the canonical one — `clear` /
 * `useSignAuthorizationToken.onSuccess` paths just invalidate it to
 * trigger a refetch.
 */
export function useAuthToken() {
  return useQuery({
    queryKey,
    queryFn: () => getAuthToken(),
  })
}

/**
 * Returns the active zone id from the cached `useAuthToken` query, or
 * `null` when no token is persisted. The wallet currently supports a
 * single zone, so this resolves to {@link zoneId} when unlocked.
 *
 * Sync sibling of {@link useAuthToken} for callers (mutation success
 * handlers, optimistic updates) that already hold a `QueryClient`.
 */
export function getActiveZoneId(queryClient: QueryClient): number | null {
  const token = queryClient.getQueryData<string | null>(queryKey)
  return token ? zoneId : null
}
